import { 
  MessageCircle, QrCode, Store, ChevronRight, Home, Search, Wallet,
  User, Coffee, Pizza, ShoppingBag, Bell, MapPin, Star, ArrowRight,
  Bot, Zap, Heart
} from 'lucide-react';
import { components, gradients } from '../theme/wunderCityTheme';

function DashboardPensacolaAI() {

  const stats = {
    totalPoints: 3847,
    dollarValue: 38.47,
    pendingPoints: 125,
    expiringSoon: 2
  };

  const recentTransactions = [
    {
      id: 1,
      business: "Joe's Crab Shack",
      amount: "$24.50",
      points: 49,
      time: "30 min ago",
      icon: "🦀"
    },
    {
      id: 2,
      business: "Coastal Coffee Co.",
      amount: "$5.75",
      points: 12,
      time: "2 hours ago",
      icon: "☕"
    },
    {
      id: 3,
      business: "Beach Books",
      amount: "$18.99",
      points: 38,
      time: "Yesterday",
      icon: "📚"
    }
  ];

  const recommendations = [
    {
      id: 1,
      name: "Angelena's Ristorante",
      category: "Italian",
      offer: "Double points tonight",
      match: "You love pasta"
    },
    {
      id: 2,
      name: "The Wine Bar",
      category: "Wine & Tapas",
      offer: "Happy hour 4-6pm",
      match: "Near your office"
    }
  ];

  return (
    <div className="w-full">
      {/* Section Header */}
      <div className="text-center px-4 md:px-8 pb-8 md:pb-12">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
          3 Approaches to a Home Dashboard<br/>w/ Pensacola AI
        </h2>
        <p className="text-lg md:text-xl text-gray-600">
          Different ways to prioritize and communicate key pieces of information quickly
        </p>
      </div>

      {/* Dashboard Screens */}
      <div className="flex flex-col lg:flex-row gap-8 p-4 md:p-8 items-center lg:justify-center overflow-x-auto">
        
        {/* Solution 1: AI-First Experience */}
        <div className="flex flex-col items-center gap-1 md:gap-2 lg:gap-4">
          <div className="relative flex-shrink-0 h-[609px] md:h-[731px] lg:h-[812px] flex items-center justify-center">
            <div className={`transform ${components.phoneMockup.scale.mobile} ${components.phoneMockup.scale.tablet} ${components.phoneMockup.scale.desktop} origin-center`}>
            <div className={components.phoneMockup.outer}>
              <div className={components.phoneMockup.inner}>
                {/* Status Bar */}
                <div className={components.statusBar.container}>
                  <span className={components.statusBar.time}>9:41</span>
                  <div className={components.statusBar.battery}>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                  </div>
                </div>

                {/* Content */}
                <div className="pt-11 h-full bg-gray-50 flex flex-col">
                  {/* Header */}
                  <div className="px-6 pt-4 pb-3 bg-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Welcome back!</p>
                        <h1 className="text-2xl font-bold text-gray-900">Good morning, Sarah</h1>
                      </div>
                      <button className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                        <Bell className="w-5 h-5 text-gray-700" />
                      </button>
                    </div>
                  </div>

                  {/* AI Assistant Prompt */}
                  <div className="px-6 py-4">
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-3xl p-5 shadow-lg">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                          <Bot className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-1">Ask Pensacola AI</h3>
                          <p className="text-purple-100 text-sm mb-3">What can I help you discover today?</p>
                          <button className="bg-white text-purple-600 px-4 py-2 rounded-xl font-medium text-sm flex items-center gap-2">
                            <MessageCircle className="w-4 h-4" />
                            Start Chat
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Rewards Balance */}
                  <div className="px-6">
                    <div className="bg-white rounded-2xl p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-sm text-gray-600">Total Rewards</p>
                          <h2 className="text-2xl font-bold text-gray-900">{stats.totalPoints.toLocaleString()} pts</h2>
                          <p className="text-sm text-sky-600">${stats.dollarValue} value</p>
                        </div>
                        <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center">
                          <Star className="w-8 h-8 text-white" />
                        </div>
                      </div>
                      {stats.expiringSoon > 0 && (
                        <div className="bg-amber-50 rounded-xl p-3 flex items-center justify-between">
                          <p className="text-sm text-amber-800">{stats.expiringSoon} rewards expiring soon</p>
                          <ChevronRight className="w-4 h-4 text-amber-600" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="px-6 mt-4">
                    <div className="grid grid-cols-3 gap-3">
                      <button className="bg-sky-500 text-white rounded-2xl p-4 flex flex-col items-center gap-2 shadow-sm">
                        <QrCode className="w-6 h-6" />
                        <span className="text-xs font-medium">Scan Reciept</span>
                      </button>
                      <button className="bg-white rounded-2xl p-4 flex flex-col items-center gap-2 shadow-sm">
                        <Store className="w-6 h-6 text-gray-700" />
                        <span className="text-xs font-medium text-gray-700">Browse</span>
                      </button>
                      <button className="bg-white rounded-2xl p-4 flex flex-col items-center gap-2 shadow-sm">
                        <Bot className="w-6 h-6 text-purple-600" />
                        <span className="text-xs font-medium text-gray-700">AI Chat</span>
                      </button>
                    </div>
                  </div>

                  {/* Recent Activity */}
                  <div className="flex-1 px-6 mt-6 overflow-y-auto pb-20">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Recent Activity</h3>
                    <div className="space-y-3">
                      {recentTransactions.map(transaction => (
                        <div key={transaction.id} className="bg-white rounded-2xl p-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center text-lg">
                              {transaction.icon}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{transaction.business}</p>
                              <p className="text-sm text-gray-600">{transaction.time}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-gray-900">{transaction.amount}</p>
                            <p className="text-sm text-green-600">+{transaction.points} pts</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Tab Bar */}
                  <div className={components.tabBar.container}>
                    <div className="flex items-center justify-around py-2">
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <Home className="w-6 h-6 text-sky-600" />
                        <span className="text-xs text-sky-600">Home</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <Search className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-600">Discover</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <Wallet className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-600">Wallet</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <User className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-600">Profile</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
          <p className={`${components.mockupLabel.className} ${components.mockupLabel.spacing.top} ${components.mockupLabel.spacing.bottom}`}>AI Assistant Focus</p>
        </div>

        {/* Solution 2: Transaction-Centric */}
        <div className="flex flex-col items-center gap-1 md:gap-2 lg:gap-4">
          <div className="relative flex-shrink-0 h-[609px] md:h-[731px] lg:h-[812px] flex items-center justify-center">
            <div className={`transform ${components.phoneMockup.scale.mobile} ${components.phoneMockup.scale.tablet} ${components.phoneMockup.scale.desktop} origin-center`}>
            <div className={components.phoneMockup.outer}>
              <div className={components.phoneMockup.inner}>
                {/* Status Bar */}
                <div className={components.statusBar.container}>
                  <span className={components.statusBar.time}>9:41</span>
                  <div className={components.statusBar.battery}>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                  </div>
                </div>

                {/* Content */}
                <div className="pt-11 h-full bg-white flex flex-col">
                  {/* Compact Header with Balance */}
                  <div className={`${gradients.skyBlue} px-6 pt-4 pb-6`}>
                    <div className="flex items-center justify-between mb-4">
                      <h1 className="text-xl font-bold text-white">Wunder City</h1>
                      <button className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                        <Bell className="w-4 h-4 text-white" />
                      </button>
                    </div>
                    <div className="text-center">
                      <p className="text-sky-100 text-sm mb-1">Your Balance</p>
                      <h2 className="text-4xl font-bold text-white mb-1">{stats.totalPoints.toLocaleString()}</h2>
                      <p className="text-sky-100">${stats.dollarValue} in rewards</p>
                    </div>
                  </div>

                  {/* Quick Actions Strip */}
                  <div className="px-6 -mt-4 mb-4">
                    <div className="bg-white rounded-2xl shadow-lg p-2 flex gap-2">
                      <button className="flex-1 bg-sky-50 text-sky-600 rounded-xl py-3 flex items-center justify-center gap-2 font-medium">
                        <QrCode className="w-5 h-5" />
                        Pay
                      </button>
                      <button className="flex-1 bg-purple-50 text-purple-600 rounded-xl py-3 flex items-center justify-center gap-2 font-medium">
                        <Bot className="w-5 h-5" />
                        AI Help
                      </button>
                      <button className="flex-1 bg-orange-50 text-orange-600 rounded-xl py-3 flex items-center justify-center gap-2 font-medium">
                        <Store className="w-5 h-5" />
                        Browse
                      </button>
                    </div>
                  </div>

                  {/* Recent Transactions Carousel */}
                  <div className="px-6 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
                      <button className="text-sky-600 text-sm font-medium">View All</button>
                    </div>
                    <div className="flex gap-3 overflow-x-auto pb-2">
                      {recentTransactions.map(transaction => (
                        <div key={transaction.id} className="flex-shrink-0 w-40 bg-gray-50 rounded-2xl p-4">
                          <div className="text-2xl mb-2">{transaction.icon}</div>
                          <p className="font-medium text-sm text-gray-900 truncate">{transaction.business}</p>
                          <p className="text-xs text-gray-600 mb-2">{transaction.time}</p>
                          <div className="flex items-center justify-between">
                            <p className="font-semibold text-sm">{transaction.amount}</p>
                            <p className="text-xs text-green-600">+{transaction.points}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="px-6 mb-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-amber-50 rounded-2xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Zap className="w-5 h-5 text-amber-600" />
                          <p className="text-sm font-medium text-amber-900">This Week</p>
                        </div>
                        <p className="text-2xl font-bold text-amber-900">+247</p>
                        <p className="text-xs text-amber-700">points earned</p>
                      </div>
                      <div className="bg-purple-50 rounded-2xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Heart className="w-5 h-5 text-purple-600" />
                          <p className="text-sm font-medium text-purple-900">Favorites</p>
                        </div>
                        <p className="text-2xl font-bold text-purple-900">12</p>
                        <p className="text-xs text-purple-700">saved places</p>
                      </div>
                    </div>
                  </div>

                  {/* Personalized Recommendations */}
                  <div className="flex-1 px-6 overflow-y-auto pb-20">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">For You</h3>
                    <div className="space-y-3">
                      {recommendations.map(rec => (
                        <div key={rec.id} className="bg-white border border-gray-100 rounded-2xl p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-semibold text-gray-900">{rec.name}</h4>
                              <p className="text-sm text-gray-600">{rec.category}</p>
                            </div>
                            <ArrowRight className="w-5 h-5 text-gray-400" />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="bg-orange-100 text-orange-700 text-xs px-2 py-1 rounded-lg font-medium">
                              {rec.offer}
                            </span>
                            <span className="text-xs text-gray-500">• {rec.match}</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* AI Assistant Card */}
                    <div className="mt-4 bg-gradient-to-r from-purple-100 to-purple-50 rounded-2xl p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center">
                          <Bot className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-purple-900">Need help finding something?</p>
                          <p className="text-sm text-purple-700">Ask Pensacola AI</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-purple-600" />
                      </div>
                    </div>
                  </div>

                  {/* Tab Bar */}
                  <div className={components.tabBar.container}>
                    <div className="flex items-center justify-around py-2">
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <Home className="w-6 h-6 text-sky-600" />
                        <span className="text-xs text-sky-600">Home</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <Search className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-600">Discover</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <Wallet className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-600">Wallet</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <User className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-600">Profile</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
          <p className={`${components.mockupLabel.className} ${components.mockupLabel.spacing.top} ${components.mockupLabel.spacing.bottom}`}>Transaction Timeline</p>
        </div>

        {/* Solution 3: Gamified Experience */}
        <div className="flex flex-col items-center gap-1 md:gap-2 lg:gap-4">
          <div className="relative flex-shrink-0 h-[609px] md:h-[731px] lg:h-[812px] flex items-center justify-center">
            <div className={`transform ${components.phoneMockup.scale.mobile} ${components.phoneMockup.scale.tablet} ${components.phoneMockup.scale.desktop} origin-center`}>
            <div className={components.phoneMockup.outer}>
              <div className={components.phoneMockup.inner}>
                {/* Status Bar */}
                <div className={components.statusBar.container}>
                  <span className={components.statusBar.time}>9:41</span>
                  <div className={components.statusBar.battery}>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                  </div>
                </div>

                {/* Content */}
                <div className="pt-11 h-full bg-gradient-to-b from-sky-50 to-white flex flex-col">
                  {/* Header */}
                  <div className="px-6 pt-4 pb-2">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold">S</span>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Level 12 Explorer</p>
                          <h1 className="text-lg font-bold text-gray-900">Sarah Chen</h1>
                        </div>
                      </div>
                      <button className="relative">
                        <Bell className="w-6 h-6 text-gray-700" />
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></div>
                      </button>
                    </div>
                  </div>

                  {/* Gamified Rewards Card */}
                  <div className="px-6">
                    <div className="bg-gradient-to-br from-amber-400 via-orange-500 to-pink-500 rounded-3xl p-5 shadow-xl">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <p className="text-amber-100 text-sm mb-1">Total Points</p>
                          <h2 className="text-4xl font-bold text-white">{stats.totalPoints.toLocaleString()}</h2>
                          <p className="text-amber-100">${stats.dollarValue} rewards</p>
                        </div>
                        <div className="text-right">
                          <div className="bg-white/20 rounded-full px-3 py-1 mb-2">
                            <p className="text-xs text-white font-medium">Next Level</p>
                          </div>
                          <p className="text-white text-sm font-medium">153 pts to go</p>
                        </div>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="bg-white/20 rounded-full h-2 mb-4">
                        <div className="bg-white rounded-full h-2 w-4/5"></div>
                      </div>

                      {/* Achievement Badges */}
                      <div className="flex gap-2">
                        <div className="bg-white/20 rounded-lg px-2 py-1 flex items-center gap-1">
                          <Coffee className="w-3 h-3 text-white" />
                          <span className="text-xs text-white">Coffee Pro</span>
                        </div>
                        <div className="bg-white/20 rounded-lg px-2 py-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-white" />
                          <span className="text-xs text-white">Downtown Regular</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons with AI */}
                  <div className="px-6 mt-4">
                    <div className="grid grid-cols-2 gap-3">
                      <button className="bg-white rounded-2xl p-4 shadow-sm flex flex-col items-center gap-2">
                        <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
                          <QrCode className="w-6 h-6 text-sky-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-900">Scan Receipt</span>
                        <span className="text-xs text-gray-600">Earn 2x pts</span>
                      </button>
                      <button className="bg-white rounded-2xl p-4 shadow-sm flex flex-col items-center gap-2">
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                          <Bot className="w-6 h-6 text-purple-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-900">Pensacola AI</span>
                        <span className="text-xs text-gray-600">Ask anything</span>
                      </button>
                    </div>
                  </div>

                  {/* Daily Challenges */}
                  <div className="px-6 mt-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Today's Challenges</h3>
                    <div className="space-y-2">
                      <div className="bg-white rounded-2xl p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                            <Coffee className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm text-gray-900">Morning Coffee Run</p>
                            <p className="text-xs text-gray-600">Visit any coffee shop</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-green-600">+50 pts</p>
                          <p className="text-xs text-gray-500">2/3</p>
                        </div>
                      </div>
                      <div className="bg-white rounded-2xl p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                            <MessageCircle className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm text-gray-900">Ask Pensacola AI</p>
                            <p className="text-xs text-gray-600">Get a local recommendation</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-purple-600">+25 pts</p>
                          <p className="text-xs text-gray-500">New!</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recent & Recommendations */}
                  <div className="flex-1 px-6 mt-4 overflow-y-auto pb-20">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Hot Spots</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white rounded-2xl p-3">
                        <div className="w-full h-20 bg-gradient-to-br from-orange-400 to-pink-400 rounded-xl mb-2 flex items-center justify-center">
                          <Pizza className="w-8 h-8 text-white" />
                        </div>
                        <p className="font-medium text-sm text-gray-900">O'Riley's</p>
                        <p className="text-xs text-gray-600">3x points today!</p>
                      </div>
                      <div className="bg-white rounded-2xl p-3">
                        <div className="w-full h-20 bg-gradient-to-br from-sky-400 to-blue-500 rounded-xl mb-2 flex items-center justify-center">
                          <ShoppingBag className="w-8 h-8 text-white" />
                        </div>
                        <p className="font-medium text-sm text-gray-900">Bay Boutique</p>
                        <p className="text-xs text-gray-600">New arrival sale</p>
                      </div>
                    </div>
                  </div>

                  {/* Tab Bar */}
                  <div className={components.tabBar.container}>
                    <div className="flex items-center justify-around py-2">
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <Home className="w-6 h-6 text-sky-600" />
                        <span className="text-xs text-sky-600">Home</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <Search className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-600">Discover</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <Wallet className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-600">Wallet</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <User className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-600">Profile</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
          <p className={`${components.mockupLabel.className} ${components.mockupLabel.spacing.top} ${components.mockupLabel.spacing.bottom}`}>Gamified Rewards</p>
        </div>

      </div>
    </div>
  );
}

export default DashboardPensacolaAI;